/**
 * RequestIdGenerator - Utility for generating unique request IDs
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for generating unique request IDs with prefixes,
 * which is useful for tracking and correlating requests across the system.
 */

/**
 * Generate a unique request ID with a prefix
 * 
 * Increments the counter and returns a formatted ID string.
 * 
 * @param counter - Reference to a counter that will be incremented
 * @param prefix - Prefix for the request ID (e.g., 'layout', 'edit', 'warm')
 * @returns Formatted request ID string (e.g., 'layout-1', 'edit-2')
 */
export function generateRequestId(counter: { value: number }, prefix: string): string {
  return `${prefix}-${++counter.value}`;
}

/**
 * Generate a unique request ID with a prefix and return both ID and counter value
 * 
 * Useful when you need both the formatted ID and the numeric counter value.
 * 
 * @param counter - Reference to a counter that will be incremented
 * @param prefix - Prefix for the request ID (e.g., 'layout', 'edit', 'warm')
 * @returns Object containing the formatted ID and the counter value
 */
export function generateRequestIdWithValue(
  counter: { value: number },
  prefix: string
): { id: string; value: number } {
  const value = ++counter.value;
  return {
    id: `${prefix}-${value}`,
    value
  };
}

/**
 * Generate a layout ID (numeric)
 * 
 * Increments the counter and returns the numeric value.
 * 
 * @param counter - Reference to a counter that will be incremented
 * @returns Numeric layout ID
 */
export function generateLayoutId(counter: { value: number }): number {
  return ++counter.value;
}

